import React, {
  FormEvent,
  useState,
  useEffect,
  RefObject,
  ChangeEvent,
  useCallback,
} from 'react'

import Button from 'tfs.button'

import NotificationInterface from '../../interfaces/client/Notification'
import PassengerSSRInterface from '../../interfaces/client/PassengerSSR'

import {
  removePassengerInputName,
  removeAllPassengersInputName,
} from '../../config/forms'

import getLabels from '../../utils/getLabels'

import EditBookingFormRowHeader from '../../containers/EditBookingFormRowHeader'
import EditBookingFormRow from '../../containers/EditBookingFormRow'

import Notifications from '../Notifications'

import styles from './EditBookingForm.module.scss'

interface EditBookingPropsInterface {
  removeNotification: Function
  removeNotifications: Function
  handleSubmit: Function
  enableDeletePassengers: Function
  disableDeletePassengers: Function
  handleDeletePassengers: Function

  formRef: RefObject<HTMLFormElement>
  formWrapperRef: RefObject<HTMLDivElement>
  flightId: string
  errors: NotificationInterface[]
  notifications: NotificationInterface[]
  passengerIds: string[]
  passengersSSRs: {
    [id: string]: PassengerSSRInterface[]
  }

  isTOFlight: boolean
  hasFieldNotification: boolean
  isDeletePassengersEnabled: boolean
  arePassengersSelectedForDeletion: boolean
}

const isNotButton = (element: any) => element.nodeName !== 'BUTTON'
const isNotSSRInput = (element: any) =>
  element.dataset.element !== 'add-ssr-input'
const isNotCheckbox = (element: any) =>
  element.dataset.element !== removePassengerInputName &&
  element.dataset.element !== removeAllPassengersInputName

const valueIsChanged = (element: any) =>
  element.defaultValue
    ? element.value !== element.defaultValue
    : element.value !== element.dataset.defaultValue

export default (props: EditBookingPropsInterface) => {
  const {
    flightId,
    isTOFlight,
    notifications,
    errors,
    removeNotification,
    removeNotifications,
    formRef,
    formWrapperRef,
    passengerIds,
    handleSubmit,
    hasFieldNotification,
    enableDeletePassengers,
    disableDeletePassengers,
    isDeletePassengersEnabled,
    handleDeletePassengers,
    arePassengersSelectedForDeletion,
    passengersSSRs,
  } = props

  const [isFormSubmittable, setIsFormSubmittable] = useState(false)

  const handleChange = useCallback(() => {
    if (hasFieldNotification) {
      setIsFormSubmittable(false)
      return
    }

    const SSRsHaveBeenChanged = Object.keys(passengersSSRs).find(
      (passengerId: string) => {
        const passengerSSRs = passengersSSRs[passengerId]

        return passengerSSRs.find(
          (passengerSSR: PassengerSSRInterface) =>
            passengerSSR.toDelete || !passengerSSR.isConfirmed
        )
      }
    )

    if (SSRsHaveBeenChanged) {
      setIsFormSubmittable(true)
      return
    }

    if (!formRef.current || !formRef.current.elements) {
      return
    }

    const formValuesChanged = Array.from(formRef.current.elements)
      .filter(isNotButton)
      .filter(isNotCheckbox)
      .filter(isNotSSRInput)
      .find(valueIsChanged)

    if (formValuesChanged && !hasFieldNotification) {
      setIsFormSubmittable(true)
      return
    }

    if (!formValuesChanged) {
      setIsFormSubmittable(false)
      return
    }
  }, [formRef, hasFieldNotification, passengersSSRs])

  useEffect(() => {
    let columnCount = 8 // css grid columns

    if (isDeletePassengersEnabled) {
      columnCount = 9
    }

    if (isTOFlight) {
      columnCount++
    }

    if (formWrapperRef.current) {
      formWrapperRef.current.style.setProperty(
        'grid-template-columns',
        `repeat(${String(columnCount)}, minmax(min-content, auto))`
      )
    }
  }, [isDeletePassengersEnabled, isTOFlight, formWrapperRef])

  useEffect(() => {
    if (hasFieldNotification) {
      setIsFormSubmittable(false)
      return
    }

    if (!formRef.current) {
      return
    }
  }, [hasFieldNotification, formRef])

  if (!passengerIds.length) {
    return (
      <div className={styles['form-fields-wrapper']}>
        <p
          className={styles['no-passengers-message']}
          data-element="no-passengers-message"
        >
          {getLabels('error_no_bookings_for_flight')}
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        setIsFormSubmittable(false)
        handleSubmit(event, flightId)
      }}
      onChange={handleChange}
      ref={formRef}
    >
      <div className={styles['form-fields-wrapper']} ref={formWrapperRef}>
        <EditBookingFormRowHeader isTOFlight={isTOFlight} />

        {notifications.length || errors.length ? (
          <span className={styles['notifications']}>
            <Notifications
              notifications={notifications}
              type="success"
              removeNotification={removeNotification}
              clearNotifications={removeNotifications}
            />

            <Notifications
              notifications={errors}
              type="error"
              removeNotification={removeNotification}
              clearNotifications={removeNotifications}
            />
          </span>
        ) : null}

        {passengerIds.map((passengerId: string, index: number) => (
          <EditBookingFormRow
            rowId={index + 1}
            key={passengerId}
            passengerId={passengerId}
            isTOFlight={isTOFlight}
            flightId={flightId}
          />
        ))}
      </div>

      <div className={styles['submit-box-wrapper']}>
        <div className={styles['submit-wrapper']}>
          {/* Left side button */}
          {isDeletePassengersEnabled ? (
            <Button
              color="white"
              data-element="disable-delete-passengers"
              name="disable-delete-passengers"
              onClick={disableDeletePassengers}
            >
              {getLabels('cancel')}
            </Button>
          ) : (
            <Button
              color="white"
              data-element="enable-delete-passengers"
              name="enable-delete-passengers"
              isDisabled={isFormSubmittable}
              onClick={enableDeletePassengers}
            >
              {getLabels('edit_passengers')}
            </Button>
          )}

          {/* Right side button */}
          {isDeletePassengersEnabled ? (
            <span className={styles['button-group']}>
              <Button
                data-element="delete-passengers-from-single-flight"
                name="delete-passengers-from-single-flight"
                isDisabled={!arePassengersSelectedForDeletion}
                onClick={(event: ChangeEvent<HTMLButtonElement>) =>
                  handleDeletePassengers(event, flightId, {
                    deletePassengerFromSingleFlight: true,
                  })
                }
              >
                {getLabels('delete_passengers_from_single_flight')}
              </Button>
            </span>
          ) : (
            <Button
              type="submit"
              data-element="update-passengers"
              name="update-passengers"
              isDisabled={!isFormSubmittable}
            >
              {getLabels('update_passengers')}
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}
